
import Vue from 'vue';
import Component from 'vue-class-component';
import { EPermissionActions } from '@/model/EPermissionActions';
import StackedColumnChart, { IStackedColumnCategory, IStackedColumnSeries } from '@/components/shared/StackedColumnChart.vue';
import { CATEGORICAL_COLORS, OTHER_SERIES_COLOR } from '@/constants/chartColors';
import { IMonthlyRegistrations, IRegistrationsSummary, reportsService } from '@/services/reportsService';

// Channels drawn with their own hue; past this the tail folds into "Others" (validated for a stacked form).
const MAX_SERIES = 5;

@Component({
  name: 'ReportsRegistrations',
  components: { StackedColumnChart },
})
export default class ReportsRegistrations extends Vue {
  readonly reportsAction = EPermissionActions.ACTION_REPORTS;

  protected monthly: IMonthlyRegistrations | null = null;
  protected monthlyLoading: boolean = false;
  protected monthlyError: string = '';

  protected startDate: string = '';
  protected endDate: string = '';
  protected summary: IRegistrationsSummary | null = null;
  protected summaryLoading: boolean = false;
  protected summaryError: string = '';

  created() {
    const today = new Date();
    this.startDate = this.isoDate(new Date(today.getFullYear(), today.getMonth(), 1));
    this.endDate = this.isoDate(today);

    this.loadMonthly();
    this.loadSummary();
  }

  get monthCategories(): IStackedColumnCategory[] {
    return this.monthly
      ? this.monthly.months.map(month => ({
        key: month,
        label: this.monthDate(month).toLocaleDateString(this.$i18n.locale, { month: 'short', year: '2-digit' }),
        longLabel: this.monthDate(month).toLocaleDateString(this.$i18n.locale, { month: 'long', year: 'numeric' }),
      }))
      : [];
  }

  // Channels come sorted by total desc: the biggest keep their own hue, the tail is summed into "Others".
  get monthSeries(): IStackedColumnSeries[] {
    if (!this.monthly) {
      return [];
    }
    const channels = this.monthly.channels;
    const ownHue = channels.length > MAX_SERIES ? channels.slice(0, MAX_SERIES - 1) : channels;
    const folded = channels.slice(ownHue.length);

    const series: IStackedColumnSeries[] = ownHue.map((channel, index) => ({
      key: channel.id,
      label: channel.name,
      color: CATEGORICAL_COLORS[index],
      values: channel.counts,
    }));

    if (folded.length) {
      series.push({
        key: 'others',
        label: this.$t('reports.registrations.monthly.others').toString(),
        sublabel: folded.map(channel => channel.name).join(', '),
        color: OTHER_SERIES_COLOR,
        values: this.monthly.months.map((_month, index) => folded.reduce((sum, channel) => sum + channel.counts[index], 0)),
      });
    }

    return series;
  }

  protected async loadMonthly() {
    this.monthlyLoading = true;
    this.monthlyError = '';
    try {
      this.monthly = await reportsService.monthlyRegistrations();
    } catch (e) {
      this.monthlyError = this.$t('reports.registrations.loadError').toString();
    } finally {
      this.monthlyLoading = false;
    }
  }

  protected async loadSummary() {
    if (!this.startDate || !this.endDate) {
      return;
    }
    this.summaryLoading = true;
    this.summaryError = '';
    try {
      this.summary = await reportsService.registrationsSummary(this.startDate, this.endDate);
    } catch (e) {
      this.summaryError = this.$t('reports.registrations.loadError').toString();
    } finally {
      this.summaryLoading = false;
    }
  }

  formatNumber(value: number): string {
    return value.toLocaleString(this.$i18n.locale);
  }

  // 'YYYY-MM' as the first local day of that month
  private monthDate(month: string): Date {
    const [year, monthNumber] = month.split('-').map(Number);

    return new Date(year, monthNumber - 1, 1);
  }

  // local calendar day as 'YYYY-MM-DD' (toISOString would shift it to UTC)
  private isoDate(date: Date): string {
    const pad = (value: number) => (value < 10 ? '0' : '') + value;

    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }
}
